/* eslint-disable react/prop-types */
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ClearIcon from "@mui/icons-material/Clear";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import MailStatusChip from "./MailStatusChip";
import { getRecipientKey } from "../mailToolUtils";

const isEmailConfirmed = (value) =>
  value === true || String(value).toLowerCase() === "true";

const getApplicationPath = (recipient) => {
  if (!recipient?.applicationId) return null;

  const applicationTypeId = Number(recipient.applicationTypeId);
  const route =
    applicationTypeId === 3 ? "student-application" : "application";

  return `/lae-miteres/${route}/${recipient.applicationId}`;
};

const RecipientsDialog = ({
  open,
  recipients,
  onClose,
  onApply,
  readOnly,
  showStatusColumn = false,
}) => {
  const [draftRecipients, setDraftRecipients] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    if (open) {
      setDraftRecipients(recipients);
      setSearch("");
      setPage(0);
    }
  }, [open, recipients]);

  const filteredRecipients = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return draftRecipients;

    return draftRecipients.filter((recipient) =>
      [
        recipient.displayName,
        recipient.email,
        recipient.amka,
        recipient.afm,
        recipient.protocolNumber,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(term)
    );
  }, [draftRecipients, search]);

  const pageRows = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredRecipients.slice(start, start + rowsPerPage);
  }, [filteredRecipients, page, rowsPerPage]);

  const selectedCount = draftRecipients.filter((recipient) => recipient.selected).length;
  const filteredKeys = useMemo(
    () => new Set(filteredRecipients.map((recipient) => getRecipientKey(recipient))),
    [filteredRecipients]
  );

  const setFilteredSelection = (selected) => {
    setDraftRecipients((prev) =>
      prev.map((recipient) =>
        filteredKeys.has(getRecipientKey(recipient))
          ? { ...recipient, selected }
          : recipient
      )
    );
  };

  const toggleRecipient = (recipient) => {
    const key = getRecipientKey(recipient);
    setDraftRecipients((prev) =>
      prev.map((item) =>
        getRecipientKey(item) === key ? { ...item, selected: !item.selected } : item
      )
    );
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  const openApplication = (recipient) => {
    const path = getApplicationPath(recipient);
    if (!path) return;

    window.open(path, "_blank", "noopener,noreferrer");
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: "min(820px, calc(100vh - 64px))",
          display: "flex",
        },
      }}
    >
      <DialogTitle>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          spacing={1}
        >
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Παραλήπτες
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedCount} επιλεγμένοι από {draftRecipients.length}
            </Typography>
          </Box>
          <Chip
            label={`${filteredRecipients.length} εμφανίζονται`}
            color="primary"
            variant="outlined"
            sx={{ alignSelf: { xs: "flex-start", sm: "center" } }}
          />
        </Stack>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", md: "center" }}
          sx={{ mb: 2 }}
        >
          <TextField
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Αναζήτηση στα αποτελέσματα"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CheckCircleOutlineIcon fontSize="small" color="disabled" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: { md: 320 } }}
          />
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<DoneAllIcon />}
              onClick={() => setFilteredSelection(true)}
              disabled={readOnly || filteredRecipients.length === 0}
            >
              Επιλογή εμφανιζόμενων
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<ClearIcon />}
              onClick={() => setFilteredSelection(false)}
              disabled={readOnly || filteredRecipients.length === 0}
            >
              Καθαρισμός εμφανιζόμενων
            </Button>
          </Stack>
        </Stack>

        <TableContainer
          sx={{
            flex: 1,
            minHeight: 0,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">Επιλογή</TableCell>
                <TableCell>Παραλήπτης</TableCell>
                <TableCell>Διεύθυνση</TableCell>
                <TableCell>ΑΜΚΑ</TableCell>
                <TableCell>ΑΦΜ</TableCell>
                {showStatusColumn && <TableCell>Κατάσταση</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {pageRows.map((recipient) => (
                <TableRow key={getRecipientKey(recipient)} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={Boolean(recipient.selected)}
                      disabled={readOnly || !recipient.recipientUserId}
                      onChange={() => toggleRecipient(recipient)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={700}>
                      {recipient.displayName || "-"}
                    </Typography>
                    {recipient.applicationId && (
                      <Box sx={{ mt: 0.25 }}>
                        <Tooltip title="Άνοιγμα αίτησης">
                          <span>
                            <Button
                              size="small"
                              variant="text"
                              endIcon={<OpenInNewIcon fontSize="inherit" />}
                              onClick={() => openApplication(recipient)}
                              sx={{
                                minWidth: 0,
                                p: 0,
                                fontSize: 12,
                                fontWeight: 600,
                                textTransform: "none",
                              }}
                            >
                              Αίτηση{recipient.protocolNumber || ""}
                            </Button>
                          </span>
                        </Tooltip>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack
                      direction="row"
                      spacing={0.75}
                      alignItems="center"
                      sx={{ minWidth: 0 }}
                    >
                      <Typography
                        variant="body2"
                        noWrap
                        title={recipient.email || ""}
                      >
                        {recipient.email || "-"}
                      </Typography>
                      {recipient.email && isEmailConfirmed(recipient.emailConfirmed) && (
                        <Tooltip title="Επιβεβαιωμένο email">
                          <CheckCircleOutlineIcon
                            fontSize="small"
                            color="success"
                            sx={{ flex: "0 0 auto" }}
                          />
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>{recipient.amka || "-"}</TableCell>
                  <TableCell>{recipient.afm || "-"}</TableCell>
                  {showStatusColumn && (
                    <TableCell>
                      <MailStatusChip
                        type="recipient"
                        statusId={recipient.statusId}
                        size="small"
                      />
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {pageRows.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={showStatusColumn ? 6 : 5}
                    align="center"
                    sx={{ py: 5 }}
                  >
                    <Typography color="text.secondary">Δεν βρέθηκαν παραλήπτες</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredRecipients.length}
          page={page}
          onPageChange={(event, nextPage) => setPage(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage="Γραμμές ανά σελίδα:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} από ${count !== -1 ? count : `πάνω από ${to}`}`
          }
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Κλείσιμο</Button>
        {!readOnly && (
          <Button
            variant="contained"
            onClick={() => onApply(draftRecipients)}
          >
            Εφαρμογή επιλογής
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RecipientsDialog;
